<!-- eslint-disable vue/no-unused-components -->
<script>
import { computed, ref, getCurrentInstance, watch } from 'vue'
import { isEqual } from 'lodash-es'

import { watchDebounced } from '@vueuse/core'

import AgGridTable from '@/components/shared-table/AgGridTable.vue'
import ChemCellRenderer from '@/components/shared-table/components/renderers/ChemCellRenderer.vue'

import GroupHeaderRenderer from '@/components/shared-table/components/renderers/GroupHeaderRenderer.vue'
import ColumnHeaderRenderer from '@/components/shared-table/components/renderers/ColumnHeaderRenderer.vue'
import ColumnHeaderFilter from '@/components/shared-table/components/renderers/ColumnHeaderFilter.vue'
import ErrorMenu from './components/ErrorMenu.vue'
import ChemPropsWithCopyCardRenderer from '@/components/shared-table/components/renderers/ChemPropsWithCopyCardRenderer.vue'
import EpiSuiteDetailDialog from '@/components/combined-predict/models/epiSuite/components/EpiSuiteDetailDialog.vue'
import WebtestTooltip from '@/components/combined-predict/models/webtest/components/WebtestTooltip.vue'
import OperaTooltip from '@/components/combined-predict/models/opera/components/OperaTooltip.vue'
import ToddsModelsTooltip from '@/components/combined-predict/models/toddsModels/components/ToddsModelsTooltip.vue'
import EpiSuiteCardTooltip from '@/components/combined-predict/models/epiSuite/components/EpiSuiteCardTooltip.vue'
import AmenabilityTooltip from '@/components/combined-predict/models/amenability/components/AmenabilityTooltip.vue'
import { buildChemicalColumnDef } from '@/components/shared-table/chemicalColumnDef'
import { buildColumnDefsTree, getModelNameById, getAllCategorizedModels } from '../../modelAdapterService'
import { buildErrorColumnDef } from './errorColumnDef'

import { rowHasAnyEstimate } from '../../utils'
import { regroupByProperty } from './utils/regroupByProperty'
import { getChemicalTitle, getChemicalUniqueKey } from '@/utils/chemical'
import { isModelColumnsVisible } from '../../modelAdapterService'

export default {
  components: {
    AgGridTable,
    ChemCellRenderer,
    ChemPropsWithCopyCardRenderer,
    agColumnHeader: ColumnHeaderRenderer,
    agColumnGroupHeader: GroupHeaderRenderer,
    ErrorMenu,
    EpiSuiteDetailDialog,
    WebtestTooltip,
    OperaTooltip,
    ToddsModelsTooltip,
    ColumnHeaderFilter,
    EpiSuiteCardTooltip,
    AmenabilityTooltip,
  },

  emits: ['selectionChanged'],

  props: {
    results: { type: Array, required: true },
    visibleColumnIds: { type: Array, default: () => [] },
    modelIds: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    displaySettings: { type: Object, required: true },
    categoriesCount: { type: Number, default: 0 },
  },

  setup(props, { emit }) {
    const agGridTableRef = ref(null)
    const hasRenderedGrid = ref(false)

    const unitOverridesByField = ref(null)

    const context = {
      headerRenderer: {
        updateFieldUnit(colId, unit) {
          unitOverridesByField.value = {
            ...(unitOverridesByField.value || {}),
            [colId]: unit,
          }
        },
      },
    }

    const getExportData = () => agGridTableRef.value?.getTableState()

    const onSelectionChanged = (nodes) => {
      emit('selectionChanged', nodes)
    }

    const columnTypes = {
      experimental: {
        cellClass: 'bg-success-light',
      },
    }

    const visibleResults = computed(() =>
      props.displaySettings?.showChemicalsWithNoEstimates
        ? props.results
        : props.results.filter((row) => rowHasAnyEstimate(row)),
    )

    watch(
      () => visibleResults.value?.length,
      (count) => {
        if (count > 0) {
          hasRenderedGrid.value = true
        }
      },
      { immediate: true },
    )

    const autoSizeAllColumns = () => {
      if (agGridTableRef.value?.autoSizeAllColumns) {
        agGridTableRef.value.autoSizeAllColumns()
      }
    }

    const showErrorColumn = computed(() => props.displaySettings.showChemicalsWithNoEstimates)

    // Watch for changes in loading state to refresh EpiSuite detail cells when loading completes
    watch(
      () => props.loading,
      (newVal, oldVal) => {
        const isEpiSuiteVisible = isModelColumnsVisible(props.visibleColumnIds, 'epiSuite')
        if (!newVal && oldVal) {
          if (showErrorColumn.value) agGridTableRef.value?.refreshCells({ columns: ['error'], force: true })
          if (isEpiSuiteVisible) agGridTableRef.value?.refreshCells({ columns: ['epiSuite::detail'], force: true })
        }
      },
    )

    watchDebounced(
      () => [
        props.visibleColumnIds,
        unitOverridesByField.value,
        props.displaySettings?.isGroupedByProperty,
        props.loading,
      ],
      (newValue, oldValue) => {
        const [oldVisibleColumnIds, oldUnitOverrides, oldIsGroupedByProperty, oldLoading] = oldValue ?? []
        const [newVisibleColumnIds, newUnitOverrides, newIsGroupedByProperty, newLoading] = newValue ?? []

        if (!isEqual(oldUnitOverrides, newUnitOverrides)) {
          return
        }

        const columnsChanged =
          !isEqual(oldVisibleColumnIds, newVisibleColumnIds) || oldIsGroupedByProperty !== newIsGroupedByProperty
        const loadingFinished = Boolean(oldLoading) && !newLoading

        if (!columnsChanged && !loadingFinished) return
        if (!visibleResults.value?.length) return

        autoSizeAllColumns()
      },
      { debounce: 200, immediate: true, deep: true },
    )

    const allCategorizedModels = computed(() => getAllCategorizedModels())

    const columnDefsByModel = computed(() => {
      const categories = allCategorizedModels.value

      const effectiveCategories =
        props.displaySettings.isGroupedByProperty && props.categoriesCount > 1
          ? regroupByProperty(categories)
          : categories

      return buildColumnDefsTree({
        categories: effectiveCategories,
        visibleColumnIds: props.visibleColumnIds,
        tableSettings: {
          showExperimental: props.displaySettings.showExperimental,
          showPredicted: props.displaySettings.showPredicted,
          unitOverridesByField: unitOverridesByField.value,
        },
      })
    })

    const proxy = getCurrentInstance().proxy

    const isMobile = computed(() => {
      return proxy.$vuetify.breakpoint.xs
    })

    const columnDefs = computed(() => {
      return [
        buildChemicalColumnDef({
          showChemCell: props.displaySettings.showChemCell,
          pinned: !isMobile.value,
        }),
        buildErrorColumnDef(showErrorColumn.value),
        ...columnDefsByModel.value,
      ]
    })

    const getRowId = (params) => {
      const chemical = params?.data?.chemical
      return chemical?.id || getChemicalUniqueKey(chemical) || String(chemical?.inputOrder ?? '')
    }

    return {
      agGridTableRef,
      getRowId,
      context,
      columnTypes,
      hasRenderedGrid,
      visibleResults,
      columnDefs,
      getExportData,
      onSelectionChanged,
      getModelNameById,
      getChemicalTitle,
      ChemCellRenderer,
      GroupHeaderRenderer,
      ErrorMenu,
    }
  },
}
</script>

<template>
  <!-- <TableCardLayout>
    <slot name="above-table" /> -->

  <AgGridTable
    ref="agGridTableRef"
    v-if="hasRenderedGrid"
    v-show="visibleResults?.length > 0"
    :column-defs="columnDefs"
    :row-data="visibleResults"
    :get-row-id="getRowId"
    :column-types="columnTypes"
    :pagination="!displaySettings.showVirtualized"
    :context="context"
    @selectionChanged="onSelectionChanged"
    class="w-100"
    style="flex-grow: 1; flex-basis: 0"
  />

  <!-- </TableCardLayout> -->
</template>

<style scoped lang="scss">
::v-deep .ag-tooltip {
  white-space: pre-line;
}

::v-deep .link-cell {
  text-decoration: underline;
  cursor: pointer;
}

// ::v-deep .tooltip-cell::after {
//   content: 'ⓘ';
//   display: inline-block;
//   text-decoration: none;
//   font-size: 0.5rem;
//   opacity: 0.5;
//   vertical-align: top;
//   padding-left: 0.25rem;
// }
// ::v-deep .tooltip-cell:hover::after {
//   opacity: 1;
// }

::v-deep .replaced-chemical-cell::before {
  content: '\21C4';
  font-family: 'Segoe UI Symbol', 'Noto Sans Symbols', 'Arial Unicode MS', sans-serif;
  display: inline-block;
  text-decoration: none;
  font-size: 0.75rem;
  opacity: 0.6;
  vertical-align: top;
  padding-left: 0.25rem;
}
::v-deep .replaced-chemical-cell:hover::before {
  opacity: 1;
}

/* ::v-deep .ag-header-cell-label {
  height: 100%;
  padding: 0 !important;
}

::v-deep .ag-header-cell-label .ag-header-cell-text {
  width: 55px;
  writing-mode: vertical-lr;
  -ms-writing-mode: tb-lr;
  line-height: 2em;
  margin-top: 60px;
}

::v-deep .ag-header-cell-label .ag-header-group-cell-label {
  height: 100%;
  padding: 0 !important;
}

::v-deep .ag-header-group-cell-label .ag-header-group-text {
  width: 55px;
  writing-mode: vertical-lr;
  -ms-writing-mode: tb-lr;
  line-height: 2em;
  margin-top: 60px;
} */

/*Necessary to allow for text to grow vertically*/

/* ::v-deep .vertical-header .ag-header-cell-label {
  height: 100%;
  padding: 0 !important;
} */

/*Force the width corresponding to how much width
    we need once the text is laid out vertically*/
/* ::v-deep .vertical-header .ag-header-cell-text {

  width: 20px;
  writing-mode: vertical-lr;
  -ms-writing-mode: tb-lr;
  line-height: 2em;
  margin-top: 5px;
} */
/*
.ag-header-cell-label .ag-header-cell-text {
 Force the width corresponding to how much width
    we need once the text is laid out vertically
  height: 100%;
  padding: 0 !important;
  transform: rotate(90deg);
  margin-top: 50px;

  Since we are rotating a span

  display: inline-block;
  width: 55px;
  writing-mode: vertical-lr;
  -ms-writing-mode: tb-lr;
  line-height: 2em;
  margin-top: 60px;
} */
</style>
